import { useState, useRef } from 'react';
import AppLogo from '../shared/AppLogo.jsx';
import { getDomainBranding, tenantUrl } from '../../tenant/tenantConfig.js';
import { getFunctionUrl } from '../../api/functionUrl.js';
import { getEnvFunctionsUrl, isEnvConfigured } from '../../api/azureClient.js';
import CredentialFlow from './CredentialFlow.jsx';
import TestEnvBadge from './TestEnvBadge.jsx';

// Environments the central login searches for a username. Production is always
// searched; 'test' is added only when a distinct test Function App is configured
// (it is, in the production build). DEV is intentionally excluded — dev tenants
// never route through login.tryggr.dk.
const LOOKUP_ENVS = ['production', ...(isEnvConfigured('test') ? ['test'] : [])];

/**
 * Look a username up across production + test Function Apps in parallel and
 * return a merged, environment-tagged tenant list. Each environment's FA filters
 * to its own registry, so test results contain only test (beta) tenants — dev
 * never appears. A failure or rate-limit in one environment never blocks the
 * other (Promise.allSettled): production login keeps working if test is down.
 *
 * Returns { tenants, rateLimited, requiresSurname, incompleteSetupCount }.
 * `rateLimited` lets the caller distinguish a genuine "no match" from a
 * throttled lookup. When a username matches more than one tenant in any
 * environment the server withholds the list and flags `requiresSurname` — the
 * caller must re-run with the user's surname to disambiguate
 * (anti-enumeration). The optional `surname` argument is forwarded to the
 * server, which then returns only the workspaces whose stored surname
 * matches. `incompleteSetupCount` (summed across environments) is set once a
 * correct surname matched at least one tenant AND another tenant sharing the
 * same username has no lastName on record there, so it can never be reached
 * via the surname gate — a setup gap for that workspace's admin to fix, not a
 * wrong guess by the user.
 */
async function lookupTenantsAcrossEnvs(username, surname) {
  let rateLimited = false;
  let requiresSurname = false;
  let incompleteSetupCount = 0;
  const results = await Promise.allSettled(
    LOOKUP_ENVS.map(async (env) => {
      const res = await fetch(`${getEnvFunctionsUrl(env)}/api/user-lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(surname ? { username, surname } : { username }),
      });
      if (res.status === 429) {
        rateLimited = true;
        return [];
      }
      if (!res.ok) return []; // treat as "no match here", don't fail the other env
      const body = await res.json();
      if (body.requiresSurname) requiresSurname = true;
      if (body.incompleteSetupCount) incompleteSetupCount += body.incompleteSetupCount;
      return (body.tenants || []).map((t) => ({ ...t, environment: env }));
    })
  );
  const tenants = results.flatMap((r) => (r.status === 'fulfilled' ? r.value : []));
  return { tenants, rateLimited, requiresSurname, incompleteSetupCount };
}

// ── Employee sign-in helpers ──────────────────────────────────────────────────
// Calls employee-lookup then employee-send-magic-link from the central login
// page. STRICTLY respects EMP-B-003: no EmployeeHub import, no portal UI,
// no Action Centre integration. Verify and portal stay on {slug}.tryggr.dk.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Universal login page — served at login.tryggr.dk.
 *
 * Flow:
 *   1. User enters username → user-lookup → find tenant(s)
 *   2a. One tenant  → go directly to CredentialFlow for that tenant
 *   2b. Many tenants → show picker, then CredentialFlow for chosen tenant
 *   3. CredentialFlow completes → mint a 60-second handoff code via auth-handoff
 *   4. Redirect to {slug}.tryggr.dk[path]#code=<code>
 *      The slug's Phase-1 consumer (useAuth session-restoration) exchanges the
 *      code and establishes the session.
 *
 * Honors ?return=<slug>&path=<pathname> (set by Phase 3) so the user lands
 * on their intended deep-link path after login.
 *
 * No auth dependencies — runs before any tenant context is established.
 */
export default function UniversalLoginPage() {
  const branding = getDomainBranding();

  // ── State ─────────────────────────────────────────────────────────────────
  const [username, setUsername] = useState('');
  const [surname, setSurname] = useState('');
  const [surnameError, setSurnameError] = useState('');
  const [screen, setScreen] = useState('form');
  // 'form' | 'loading' | 'not-found' | 'surname' | 'pick' | 'credentials' | 'minting' | 'error'
  const [tenants, setTenants] = useState([]);
  const [activeTenant, setActiveTenant] = useState(null);
  const [lookupError, setLookupError] = useState('');
  const [mintError, setMintError] = useState('');
  // Count of other tenants sharing this username whose admin hasn't entered
  // a last name yet — set only once a correct surname matched >=1 tenant.
  const [incompleteSetupCount, setIncompleteSetupCount] = useState(0);

  // Phase 3 bounce params — ?return=<slug> identifies which workspace sent
  // the user here; ?path= is already consumed by handleCredentialSuccess.
  const returnSlug = new URLSearchParams(window.location.search).get('return') || '';

  const inputRef = useRef(null);
  const surnameInputRef = useRef(null);

  // ── Employee sign-in state ─────────────────────────────────────────────────
  // 'off' = not in employee mode; 'form' | 'loading' | 'pick' | 'sent'
  const [empMode, setEmpMode] = useState('off');
  const [empEmail, setEmpEmail] = useState('');
  const [empError, setEmpError] = useState('');
  const [empTenants, setEmpTenants] = useState([]);
  const empInputRef = useRef(null);

  // ── Shared styles ─────────────────────────────────────────────────────────

  const outer = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--bg)',
    fontFamily: 'var(--font-body)',
  };
  const inner = { width: '100%', maxWidth: 400, padding: '0 24px' };
  const monoLabel = {
    display: 'block',
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--fg3)',
    fontFamily: 'var(--font-mono)',
    marginBottom: 6,
  };

  function Header() {
    return (
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        <AppLogo size={52} borderRadius={14} style={{ margin: '0 auto 16px' }} />
        <div className="display" style={{ fontSize: 26, fontWeight: 600, color: 'var(--fg)' }}>
          {branding.name}
        </div>
        <div
          style={{
            fontSize: 12,
            color: 'var(--fg3)',
            marginTop: 4,
            fontFamily: 'var(--font-mono)',
          }}
        >
          Compliance Management Platform
        </div>
      </div>
    );
  }

  // ── Lookup ────────────────────────────────────────────────────────────────

  async function handleLookup() {
    const trimmed = username.trim();
    if (!trimmed) return;

    setScreen('loading');
    setLookupError('');
    setIncompleteSetupCount(0);

    try {
      const {
        tenants: found,
        rateLimited,
        requiresSurname,
      } = await lookupTenantsAcrossEnvs(trimmed);

      // Multiple matches — the server withheld the list. Ask for the surname
      // before revealing anything (anti-enumeration). Also gate the cross-env
      // case where each environment returned a single match but the merged
      // total is >1, so the same protection applies end to end.
      if (requiresSurname || (found && found.length > 1)) {
        setSurname('');
        setSurnameError('');
        setScreen('surname');
        setTimeout(() => surnameInputRef.current?.focus(), 0);
        return;
      }

      if (!found || found.length === 0) {
        if (rateLimited) {
          setLookupError('Too many attempts. Please wait a moment and try again.');
          setScreen('form');
          return;
        }
        setScreen('not-found');
        return;
      }

      // Single tenant — go straight to credentials.
      setActiveTenant(found[0]);
      setScreen('credentials');
    } catch (err) {
      setLookupError(err.message || 'Something went wrong. Please try again.');
      setScreen('form');
    }
  }

  // Second-factor surname submission for multi-tenant usernames. The server
  // returns only the workspaces whose stored surname matches what was entered.
  async function handleSurnameSubmit() {
    const trimmedSurname = surname.trim();
    if (!trimmedSurname) {
      setSurnameError('Please enter your last name.');
      return;
    }
    setScreen('loading');
    setSurnameError('');
    setIncompleteSetupCount(0);

    try {
      const {
        tenants: found,
        rateLimited,
        incompleteSetupCount: incomplete,
      } = await lookupTenantsAcrossEnvs(username.trim(), trimmedSurname);

      if (!found || found.length === 0) {
        setScreen('surname');
        setTimeout(() => surnameInputRef.current?.focus(), 0);
        setSurnameError(
          rateLimited
            ? 'Too many attempts. Please wait a moment and try again.'
            : 'No workspace matched that name. Check the spelling, or contact your administrator.'
        );
        return;
      }

      setIncompleteSetupCount(incomplete || 0);

      // If the user was bounced from a specific slug, auto-select it.
      if (returnSlug) {
        const returnTenant = found.find((t) => t.slug === returnSlug);
        if (returnTenant) {
          setActiveTenant(returnTenant);
          setScreen('credentials');
          return;
        }
      }

      if (found.length === 1) {
        setActiveTenant(found[0]);
        setScreen('credentials');
        return;
      }

      setTenants(found);
      setScreen('pick');
    } catch (err) {
      setScreen('surname');
      setSurnameError(err.message || 'Something went wrong. Please try again.');
    }
  }

  function handleSurnameKeyDown(e) {
    if (e.key === 'Enter') handleSurnameSubmit();
  }

  function handlePickTenant(tenant) {
    setActiveTenant(tenant);
    setScreen('credentials');
  }

  function handleBack() {
    setScreen('form');
    setLookupError('');
    setTenants([]);
    setActiveTenant(null);
    setMintError('');
    setSurname('');
    setSurnameError('');
    setIncompleteSetupCount(0);
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') handleLookup();
  }

  // ── Employee sign-in ──────────────────────────────────────────────────────
  // EMP-B-003: thin email-only form; resolves tenant and triggers magic-link
  // email. Verify and portal stay on {slug}.tryggr.dk — never imported here.

  function openEmployeeMode() {
    setEmpMode('form');
    setEmpEmail('');
    setEmpError('');
    setEmpTenants([]);
    setTimeout(() => empInputRef.current?.focus(), 0);
  }

  function closeEmployeeMode() {
    setEmpMode('off');
    setEmpEmail('');
    setEmpError('');
    setEmpTenants([]);
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  async function sendEmployeeMagicLink(tenantId) {
    try {
      const send = () =>
        fetch(getFunctionUrl('employee-send-magic-link'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tenantId, email: empEmail.trim().toLowerCase() }),
        });
      // A 409 means the employee record was saved by someone else while the
      // link was being minted, and no email went out. This screen shows
      // "check your email" whatever happens (no enumeration), so retry once
      // silently rather than leave the user waiting for a mail that never comes.
      const res = await send();
      if (res && res.status === 409) await send();
    } catch {
      // Non-fatal — we always show the "check your email" screen regardless
      // to prevent enumeration. The magic link is sent server-side.
    }
  }

  async function handleEmployeeLookup() {
    const trimmed = empEmail.trim().toLowerCase();
    if (!trimmed || !EMAIL_RE.test(trimmed)) {
      setEmpError('Please enter a valid email address.');
      return;
    }
    setEmpError('');
    setEmpMode('loading');

    try {
      const res = await fetch(getFunctionUrl('employee-lookup'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });

      if (res.status === 429) {
        setEmpMode('form');
        setEmpError('Too many requests. Please wait a moment and try again.');
        return;
      }

      const { tenants: found } = res.ok ? await res.json() : { tenants: [] };

      if (!found || found.length <= 1) {
        // 0 or 1 tenants — always proceed to send (silent when 0, to prevent
        // enumeration). employee-send-magic-link handles the not-found case.
        if (found && found.length === 1) {
          await sendEmployeeMagicLink(found[0].id);
        }
        setEmpMode('sent');
        return;
      }

      // Multiple tenants — show picker so the employee chooses which org
      setEmpTenants(found);
      setEmpMode('pick');
    } catch {
      // Show confirmation regardless of network error to prevent enumeration
      setEmpMode('sent');
    }
  }

  async function handleEmployeePickTenant(tenant) {
    setEmpMode('loading');
    await sendEmployeeMagicLink(tenant.id);
    setEmpMode('sent');
  }

  function handleEmployeeKeyDown(e) {
    if (e.key === 'Enter') handleEmployeeLookup();
  }

  // ── Handoff mint + redirect ────────────────────────────────────────────────

  // Function App base URL for the selected tenant's environment. Undefined for
  // production tenants → CredentialFlow/mint fall back to the build-time
  // (production) FA, identical to prior behaviour. For test (beta) tenants it
  // points the whole credential flow + handoff mint at the test FA.
  const activeBaseUrl =
    activeTenant && activeTenant.environment === 'test' ? getEnvFunctionsUrl('test') : undefined;

  async function handleCredentialSuccess(token) {
    setScreen('minting');
    setMintError('');

    try {
      // Mint the handoff code on the SAME environment's Function App the
      // credentials were verified against, so the code lands in that env's
      // auth_handoff_codes table. The slug's SWA (test SWA for beta-*) then
      // exchanges it against the same env's FA. AUTH-B-008: tenant binding
      // still comes only from the verified JWT.
      const handoffUrl = activeBaseUrl
        ? `${activeBaseUrl}/api/auth-handoff`
        : getFunctionUrl('auth-handoff');
      const res = await fetch(handoffUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'mint' }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Handoff failed (${res.status})`);
      }

      const { code } = await res.json();

      // Build redirect URL, honouring an optional ?path= deep-link hint.
      const params = new URLSearchParams(window.location.search);
      const returnPath = params.get('path') || '';
      const cleanPath = returnPath
        ? returnPath.startsWith('/')
          ? returnPath
          : `/${returnPath}`
        : '';

      const base = tenantUrl(activeTenant.slug);
      window.location.href = `${base}${cleanPath}#code=${encodeURIComponent(code)}`;
    } catch (err) {
      console.error('[UniversalLoginPage] mint error:', err);
      setMintError(err.message || 'Sign-in succeeded but the redirect failed. Please try again.');
      setScreen('credentials');
    }
  }

  // ── Employee sign-in screens ──────────────────────────────────────────────

  if (empMode === 'loading') {
    return (
      <div style={outer}>
        <div style={inner}>
          <Header />
          <div style={{ textAlign: 'center', color: 'var(--fg3)', fontSize: 13 }}>
            Sending sign-in link…
          </div>
        </div>
      </div>
    );
  }

  if (empMode === 'sent') {
    return (
      <div style={outer}>
        <div style={inner}>
          <Header />
          <div className="card" style={{ padding: 32 }}>
            <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 600, color: 'var(--fg)' }}>
              Check your email
            </div>
            <div style={{ fontSize: 13, color: 'var(--fg3)', lineHeight: 1.6, marginBottom: 24 }}>
              If <strong style={{ color: 'var(--fg)' }}>{empEmail.trim()}</strong> is registered as
              an employee, you&apos;ll receive a sign-in link shortly. The link expires in
              15&nbsp;minutes.
            </div>
            <button
              className="btn"
              style={{ width: '100%' }}
              onClick={() => {
                setEmpMode('form');
                setEmpEmail('');
                setTimeout(() => empInputRef.current?.focus(), 0);
              }}
            >
              Try a different email
            </button>
          </div>
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <button
              onClick={closeEmployeeMode}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--fg3)',
                fontSize: 12,
                cursor: 'pointer',
                fontFamily: 'var(--font-mono)',
                padding: 0,
              }}
            >
              ← Back to admin sign-in
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (empMode === 'pick') {
    return (
      <div style={outer}>
        <div style={inner}>
          <Header />
          <div className="card" style={{ padding: 32 }}>
            <div style={{ marginBottom: 4, fontSize: 14, fontWeight: 600, color: 'var(--fg)' }}>
              Choose your workspace
            </div>
            <div
              style={{
                fontSize: 12,
                color: 'var(--fg3)',
                marginBottom: 20,
                fontFamily: 'var(--font-mono)',
              }}
            >
              {empEmail.trim()}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {empTenants.map((t) => (
                <button
                  key={t.id}
                  onClick={() => handleEmployeePickTenant(t)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    borderRadius: 8,
                    background: 'var(--bg2)',
                    border: '1px solid var(--border)',
                    cursor: 'pointer',
                    color: 'var(--fg)',
                    fontSize: 14,
                    fontFamily: 'var(--font-body)',
                    textAlign: 'left',
                    transition: 'border-color 0.15s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
                  onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
                >
                  <span style={{ fontWeight: 500 }}>{t.name}</span>
                  <span
                    style={{ fontSize: 11, color: 'var(--fg3)', fontFamily: 'var(--font-mono)' }}
                  >
                    {t.slug}
                  </span>
                </button>
              ))}
            </div>
            <button
              onClick={() => {
                setEmpMode('form');
                setEmpTenants([]);
              }}
              style={{
                marginTop: 20,
                background: 'none',
                border: 'none',
                color: 'var(--fg3)',
                fontSize: 12,
                cursor: 'pointer',
                fontFamily: 'var(--font-mono)',
                padding: 0,
              }}
            >
              ← Use a different email
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (empMode === 'form') {
    return (
      <div style={outer}>
        <div style={inner}>
          <Header />
          <div className="card" style={{ padding: 32 }}>
            <div style={{ marginBottom: 4, fontSize: 14, fontWeight: 600, color: 'var(--fg)' }}>
              Employee sign-in
            </div>
            <div style={{ fontSize: 12, color: 'var(--fg3)', marginBottom: 20, lineHeight: 1.5 }}>
              Enter your work email and we&apos;ll send a one-time sign-in link to your inbox.
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={monoLabel}>Work email</label>
              <input
                ref={empInputRef}
                className="input"
                type="email"
                autoComplete="email"
                placeholder="you@company.com"
                value={empEmail}
                onChange={(e) => {
                  setEmpEmail(e.target.value);
                  setEmpError('');
                }}
                onKeyDown={handleEmployeeKeyDown}
                style={{ width: '100%' }}
              />
            </div>
            {empError && (
              <div
                style={{
                  marginBottom: 16,
                  padding: '10px 14px',
                  borderRadius: 8,
                  background: 'var(--danger-bg, rgba(239,68,68,0.1))',
                  border: '1px solid var(--danger-border, rgba(239,68,68,0.25))',
                  color: 'var(--danger)',
                  fontSize: 13,
                }}
              >
                {empError}
              </div>
            )}
            <button
              className="btn btn-primary"
              style={{ width: '100%' }}
              disabled={!empEmail.trim()}
              onClick={handleEmployeeLookup}
            >
              Send sign-in link
            </button>
          </div>
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <button
              onClick={closeEmployeeMode}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--fg3)',
                fontSize: 12,
                cursor: 'pointer',
                fontFamily: 'var(--font-mono)',
                padding: 0,
              }}
            >
              ← Back to admin sign-in
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Loading ───────────────────────────────────────────────────────────────

  if (screen === 'loading' || screen === 'minting') {
    return (
      <div style={outer}>
        <div style={inner}>
          <Header />
          <div style={{ textAlign: 'center', color: 'var(--fg3)', fontSize: 13 }}>
            {screen === 'minting' ? 'Completing sign-in…' : 'Looking up your account…'}
          </div>
        </div>
      </div>
    );
  }

  // ── Not found ─────────────────────────────────────────────────────────────

  if (screen === 'not-found') {
    return (
      <div style={outer}>
        <div style={inner}>
          <Header />
          <div className="card" style={{ padding: 32 }}>
            <div style={{ marginBottom: 12, fontSize: 14, fontWeight: 600, color: 'var(--fg)' }}>
              No account found
            </div>
            <div style={{ fontSize: 13, color: 'var(--fg3)', lineHeight: 1.6, marginBottom: 24 }}>
              We couldn&apos;t find a workspace for{' '}
              <strong style={{ color: 'var(--fg)' }}>{username.trim()}</strong>.<br />
              Check the spelling, or contact your administrator.
            </div>
            <button className="btn" style={{ width: '100%' }} onClick={handleBack}>
              Try a different username
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Surname second factor (multi-tenant username) ─────────────────────────

  if (screen === 'surname') {
    return (
      <div style={outer}>
        <div style={inner}>
          <Header />
          <div className="card" style={{ padding: 32 }}>
            <div style={{ marginBottom: 4, fontSize: 14, fontWeight: 600, color: 'var(--fg)' }}>
              Confirm your identity
            </div>
            <div style={{ fontSize: 12, color: 'var(--fg3)', marginBottom: 20, lineHeight: 1.5 }}>
              This sign-in is linked to more than one workspace. Enter your last name to continue to
              the right one.
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={monoLabel}>Last name</label>
              <input
                ref={surnameInputRef}
                className="input"
                type="text"
                autoComplete="family-name"
                placeholder="Your last name"
                value={surname}
                onChange={(e) => {
                  setSurname(e.target.value);
                  setSurnameError('');
                }}
                onKeyDown={handleSurnameKeyDown}
                style={{ width: '100%' }}
              />
            </div>
            {surnameError && (
              <div
                style={{
                  marginBottom: 16,
                  padding: '10px 14px',
                  borderRadius: 8,
                  background: 'var(--danger-bg, rgba(239,68,68,0.1))',
                  border: '1px solid var(--danger-border, rgba(239,68,68,0.25))',
                  color: 'var(--danger)',
                  fontSize: 13,
                }}
              >
                {surnameError}
              </div>
            )}
            <button
              className="btn btn-primary"
              style={{ width: '100%' }}
              disabled={!surname.trim()}
              onClick={handleSurnameSubmit}
            >
              Continue
            </button>
            <button
              onClick={handleBack}
              style={{
                marginTop: 20,
                background: 'none',
                border: 'none',
                color: 'var(--fg3)',
                fontSize: 12,
                cursor: 'pointer',
                fontFamily: 'var(--font-mono)',
                padding: 0,
              }}
            >
              ← Use a different username
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Incomplete-setup notice ──────────────────────────────────────────────
  // Shown on the picker and credentials screens when the surname lookup
  // proved this username has at least one other tenant it could not surface
  // because that tenant never recorded a last name for this user.

  function IncompleteSetupNotice() {
    if (!incompleteSetupCount) return null;
    const plural = incompleteSetupCount > 1;
    return (
      <div
        style={{
          marginBottom: 16,
          padding: '12px 14px',
          borderRadius: 8,
          background: 'var(--warn-bg)',
          border: '1px solid var(--warn)',
          color: 'var(--fg)',
          fontSize: 12.5,
          lineHeight: 1.5,
        }}
      >
        We may have a setup issue: you appear to have access to {incompleteSetupCount} more
        workspace{plural ? 's' : ''} we can't show you yet, because your name hasn't been entered
        there. Contact the administrator of {plural ? 'those workspaces' : 'that workspace'} and ask
        them to add your first and last name under Settings → Users, then log in again.
      </div>
    );
  }

  // ── Tenant picker ─────────────────────────────────────────────────────────

  if (screen === 'pick') {
    return (
      <div style={outer}>
        <div style={inner}>
          <Header />
          <div className="card" style={{ padding: 32 }}>
            <IncompleteSetupNotice />
            <div style={{ marginBottom: 4, fontSize: 14, fontWeight: 600, color: 'var(--fg)' }}>
              Choose your workspace
            </div>
            <div
              style={{
                fontSize: 12,
                color: 'var(--fg3)',
                marginBottom: 20,
                fontFamily: 'var(--font-mono)',
              }}
            >
              {username.trim()}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {tenants.map((t) => (
                <button
                  key={`${t.environment || 'production'}-${t.id}`}
                  onClick={() => handlePickTenant(t)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    borderRadius: 8,
                    background: 'var(--bg2)',
                    border: '1px solid var(--border)',
                    cursor: 'pointer',
                    color: 'var(--fg)',
                    fontSize: 14,
                    fontFamily: 'var(--font-body)',
                    textAlign: 'left',
                    transition: 'border-color 0.15s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
                  onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500 }}>
                    {t.name}
                    {t.environment === 'test' && <TestEnvBadge style={{ marginTop: 0 }} />}
                  </span>
                  <span
                    style={{ fontSize: 11, color: 'var(--fg3)', fontFamily: 'var(--font-mono)' }}
                  >
                    {t.slug}
                  </span>
                </button>
              ))}
            </div>
            <button
              onClick={handleBack}
              style={{
                marginTop: 20,
                background: 'none',
                border: 'none',
                color: 'var(--fg3)',
                fontSize: 12,
                cursor: 'pointer',
                fontFamily: 'var(--font-mono)',
                padding: 0,
              }}
            >
              ← Use a different username
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Credentials (full flow for the chosen tenant) ─────────────────────────
  //
  // CredentialFlow drives password / MFA verify / MFA setup / grace / etc.
  // On completion it returns the session JWT; we mint a handoff code and
  // redirect the user to their tenant subdomain.

  if (screen === 'credentials' && activeTenant) {
    return (
      <>
        {(mintError || incompleteSetupCount > 0) && (
          <div
            style={{
              position: 'fixed',
              top: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 999,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              width: 360,
              maxWidth: 'calc(100vw - 32px)',
            }}
          >
            {mintError && (
              <div
                style={{
                  padding: '10px 16px',
                  borderRadius: 8,
                  background: 'var(--danger-bg)',
                  border: '1px solid rgba(251,113,133,0.25)',
                  color: 'var(--danger)',
                  fontSize: 13,
                  textAlign: 'center',
                }}
              >
                {mintError}
              </div>
            )}
            {incompleteSetupCount > 0 && <IncompleteSetupNotice />}
          </div>
        )}
        <CredentialFlow
          tenantId={activeTenant.id}
          initialUsername={username.trim()}
          autoPasswordless
          functionsBaseUrl={activeBaseUrl}
          environment={activeTenant.environment || 'production'}
          onComplete={(token) => handleCredentialSuccess(token)}
          onBack={handleBack}
        />
      </>
    );
  }

  // ── Form (default / username entry) ───────────────────────────────────────

  return (
    <div style={outer}>
      <div style={inner}>
        <Header />
        <div className="card" style={{ padding: 32 }}>
          <div style={{ marginBottom: 20 }}>
            <label style={monoLabel}>Email address</label>
            <input
              ref={inputRef}
              className="input"
              type="email"
              autoComplete="email"
              autoFocus
              placeholder="you@company.com"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyDown={handleKeyDown}
              style={{ width: '100%' }}
            />
          </div>

          {lookupError && (
            <div
              style={{
                marginBottom: 16,
                padding: '10px 14px',
                borderRadius: 8,
                background: 'var(--danger-bg, rgba(239,68,68,0.1))',
                border: '1px solid var(--danger-border, rgba(239,68,68,0.25))',
                color: 'var(--danger)',
                fontSize: 13,
              }}
            >
              {lookupError}
            </div>
          )}

          <button
            className="btn btn-primary"
            style={{ width: '100%' }}
            disabled={!username.trim()}
            onClick={handleLookup}
          >
            Continue
          </button>
        </div>

        <div style={{ marginTop: 20, textAlign: 'center', fontSize: 12, color: 'var(--fg3)' }}>
          Know your workspace?{' '}
          <span style={{ color: 'var(--fg3)' }}>
            Go directly to{' '}
            <span style={{ fontFamily: 'var(--font-mono)' }}>yourcompany.tryggr.dk</span>
          </span>
        </div>

        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <button
            onClick={openEmployeeMode}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--fg3)',
              fontSize: 12,
              cursor: 'pointer',
              fontFamily: 'var(--font-mono)',
              padding: 0,
              textDecoration: 'underline',
            }}
          >
            Employee? Sign in via portal →
          </button>
        </div>
      </div>
    </div>
  );
}
